import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import useDebounce from 'common/hooks/useDebounce';
import { useGetProspects } from 'common/queries-and-mutations/admin/prospect';
import { Loader } from 'components/Loader';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import AdminTableProperties from 'components/TableWidget/AdminTableProperties';
import Paginator from 'components/TableWidget/Paginator';
import { PROSPECT_TABLE_REPORT } from 'constants/download-table-header';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Prospect } from 'service/dto/prospect.dto';

export default function Prospects() {
  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;
  const searchQueryString = urlParams.get('search_query') ?? '';
  const debounceSearch = useDebounce(searchQueryString, 1300);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);

  const { data: prospectData, isLoading, isFetched } = useGetProspects();

  useEffect(() => {
    if (debounceSearch.length > 3) {
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  const columnHelper = createColumnHelper<Prospect>();

  const columns = [
    columnHelper.accessor(row => row.othername, {
      id: 'othername',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">First name</span>
      )
    }),
    columnHelper.accessor(row => row.surname, {
      id: 'surname',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Last name</span>
      )
    }),
    columnHelper.accessor(row => row.type, {
      id: 'type',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Type</span>
      )
    }),
    columnHelper.accessor(row => row.prospect_id, {
      id: 'prospect_id',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Prospect ID
        </span>
      )
    }),
    columnHelper.accessor(row => row.date, {
      id: 'date',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Date</span>
      )
    }),
    columnHelper.accessor(row => row.lastActivity, {
      id: 'lastActivity',
      cell: info => (
        <div className="column-to-hide">
          <Link
            to={`details/${info.row.original.prospect_id}`}
            className="rounded-md bg-primary-900 p-2 px-5 text-white underline"
          >
            View
          </Link>
        </div>
      ),
      header: () => (
        <span className="column-to-hide text-xs font-semibold text-[#1F4173]">
          Action
        </span>
      )
    })
  ];

  const totalPages = Math.ceil(
    (prospectData?.prospects ?? []).length / Number(numberOfItemsPerPage)
  );
  const startIndex = (Number(currentPage) - 1) * Number(numberOfItemsPerPage);
  const endIndex = Math.min(
    startIndex + Number(numberOfItemsPerPage),
    (prospectData?.prospects ?? []).length
  );

  const data = useMemo<Prospect[]>(() => {
    if (isExecutingSearch) {
      return (prospectData?.prospects ?? [])
        .filter(
          item =>
            item.surname
              .toLowerCase()
              .includes(searchQueryString.toLowerCase()) ||
            item.othername
              .toLowerCase()
              .includes(searchQueryString.toLowerCase()) ||
            item.prospect_id
              .toString()
              .includes(searchQueryString.toLowerCase())
        )
        .slice(startIndex, endIndex)
        .map(item => ({
          prospect_id: item.prospect_id,
          surname: item.surname,
          othername: item.othername,
          date: item.date,
          time: item.time,
          type: item.type,
          lastActivity: item.lastActivity,
          myProspect: item.myProspect
        }));
    }
    return (prospectData?.prospects ?? [])
      .slice(startIndex, endIndex)
      .map(item => ({
        prospect_id: item.prospect_id,
        surname: item.surname,
        othername: item.othername,
        date: item.date,
        time: item.time,
        type: item.type,
        lastActivity: item.lastActivity,
        myProspect: item.myProspect
      }));
  }, [
    isExecutingSearch,
    prospectData?.prospects,
    startIndex,
    endIndex,
    searchQueryString
  ]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      urlParams.set('page', '1');
      urlParams.set('items_per_page', '10');
      setUrlParams(urlParams);
    }
  };

  if (isLoading && isFetched) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb title="Prospects" />
      <AdminTableProperties
        searchQuery={searchQueryString}
        handleSearch={handleChange}
        exportData={prospectData?.prospects ?? []}
        exportHeader={PROSPECT_TABLE_REPORT}
        fileName={'prospects'}
        showFilter={false}
      />

      <div className="mb-20 mt-3 flex flex-col overflow-auto rounded-[5px] bg-white pb-20">
        <table
          className={clsx({
            'w-full min-w-full table-to-print': true,
            'flexs flex-col h-[30rem]': table.getRowModel().rows.length === 0
          })}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="border-b  text-xs text-[#1F4173]"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="p-5 text-left font-medium text-[#1F4173]"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex'
                            : '',
                          onClick: header.column.getToggleSortingHandler()
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          // asc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // ),
                          // desc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // )
                          asc: <></>,
                          desc: <></>
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr className="mt-10 pt-10 text-center text-xl font-bold">
                <td colSpan={7}>No prospect found</td>
              </tr>
            )}
            {table.getRowModel().rows.map((row, cellIndex) => (
              <tr key={row.id} className="">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx({
                      'p-5 text-xs': true,
                      'bg-[#F9F9F9]': cellIndex % 2 !== 0
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-5 p-5">
          <Paginator
            numberOfPages={totalPages}
            page={+currentPage}
            hasNext={totalPages > Number(currentPage)}
            hasPrevious={Number(currentPage) > 1}
            numberOfItemsPerPage={+numberOfItemsPerPage}
            setNumberOfItemsPerPage={num => {
              // setPageSize(num);
              urlParams.set('items_per_page', num.toString());
              setUrlParams(urlParams);
            }}
            goToNextPage={goToNextPage}
            goToPreviousPage={goToPreviousPage}
            showPageNumber={false}
          />
        </div>
      </div>
    </div>
  );
}
